import { useCallback, useEffect, useRef, useState } from 'react'
import {
  LiveKitRoom, RoomAudioRenderer, VideoTrack, useConnectionState, useDataChannel, useLocalParticipant,
  useRemoteParticipants, useRoomContext, useTracks,
} from '@livekit/components-react'
import { ConnectionState, Track, VideoPresets } from 'livekit-client'
import { LiveKitDeviceSettingsButton } from '@astro/device-settings/livekit'
import { LuMicOff, LuPhone, LuScreenShare, LuVideoOff } from 'react-icons/lu'
import ConsultationRoomView from './ConsultationRoomView'
import { parseMeetingStartedAt, useVideoPortrait } from './consultationShared'

/**
 * The consultation room on LiveKit — the twin of VideosdkConsultationRoom on
 * the same shared ConsultationRoomView, driven by LiveKit's hooks instead of
 * VideoSDK's. Mounted when the server says the booking runs on LiveKit
 * (session.provider === 'livekit'); the token, room name and server URL all
 * come from GET /bookings/:id/call/join, bound to this booking + participant.
 *
 * What differs from the VideoSDK twin, and how it is bridged:
 *  - no client-side "end meeting": the host asks the server to close the room
 *    (onEndCall) and also broadcasts END over the data channel, then disconnects;
 *  - remote audio is one <RoomAudioRenderer /> at the room level (so no
 *    RemoteAudio element in the tiles);
 *  - the shared session clock rides participant ATTRIBUTES (presence, replayed
 *    to late joiners) instead of VideoSDK's persisted pubsub.
 */

/** Participant attribute carrying the session anchor (epoch ms, earliest wins). */
const ATTR_CLOCK = 'consultClock'
/** Data-channel topic for in-call commands (END). */
const CONTROL_TOPIC = 'consult-control'

export default function LiveKitConsultationRoom({ session, booking, role, backPath, audioOnly = false, onEndCall }) {
  // The host connects on their explicit Join; the seeker already confirmed
  // (invite popup / Join Now), so their room connects at once.
  const [connect, setConnect] = useState(role === 'seeker')
  // The view's lifecycle handlers, read by the Room callbacks through a ref.
  const handlersRef = useRef({})

  return (
    <LiveKitRoom
      serverUrl={session.serverUrl}
      token={session.token}
      connect={connect}
      audio
      video={!audioOnly}
      options={{
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
      }}
      onDisconnected={() => handlersRef.current.onLeft?.()}
      onError={(err) => handlersRef.current.onError?.(err)}
    >
      {/* Every remote audio track (mic + shared tab/system audio) plays through
          this one renderer. Local tracks are never rendered, so the mic can't
          be fed back into the speakers. */}
      <RoomAudioRenderer />
      <LiveKitRoomBridge
        booking={booking}
        role={role}
        backPath={backPath}
        audioOnly={audioOnly}
        counterpartId={session.counterpartUserId ?? null}
        onEndCall={onEndCall}
        connect={connect}
        requestConnect={() => setConnect(true)}
        handlersRef={handlersRef}
      />
    </LiveKitRoom>
  )
}

function LiveKitRoomBridge({ booking, connect, requestConnect, handlersRef, ...rest }) {
  const media = useLivekitMedia({ booking, connect, requestConnect, handlersRef })
  return <ConsultationRoomView booking={booking} provider="livekit" media={media} {...rest} />
}

/** LiveKit's hooks, shaped into the view's `media` contract. */
function useLivekitMedia({ booking, connect, requestConnect, handlersRef }) {
  const room = useRoomContext()
  const connectionState = useConnectionState(room)
  const connected = connectionState === ConnectionState.Connected
  const phase = connected ? 'joined' : connect ? 'joining' : 'lobby'

  useEffect(() => {
    if (connected) handlersRef.current.onJoined?.()
  }, [connected, handlersRef])

  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant()
  const remoteParticipants = useRemoteParticipants()
  // A 1-1 call: the first remote participant IS the other party.
  const remote = remoteParticipants[0] ?? null
  const localParticipantId = connected ? (localParticipant?.identity ?? null) : null
  const remoteParticipantId = remote?.identity ?? null

  const shareTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: false })
  const presenterId =
    shareTracks.find((t) => t.publication && !t.publication.isMuted)?.participant?.identity ?? null

  // ── Shared session clock ──
  // Seeded from the booking's meeting_started_at (covers reloads mid-call).
  // Whoever anchors first mirrors the moment onto their own presence
  // attributes; everyone reads every participant's attribute each second and
  // converges on the EARLIEST anchor, so both countdowns agree.
  const [anchorMs, setAnchorMs] = useState(() => parseMeetingStartedAt(booking))
  const anchorRef = useRef(anchorMs)
  anchorRef.current = anchorMs
  const adopt = useCallback((ts) => {
    if (!Number.isFinite(ts) || ts <= 0) return
    if (anchorRef.current == null || ts < anchorRef.current) {
      anchorRef.current = ts
      setAnchorMs(ts)
    }
  }, [])
  useEffect(() => {
    // Imperative read — attribute changes don't re-render this component.
    const tick = () => {
      for (const p of [room.localParticipant, ...room.remoteParticipants.values()]) {
        adopt(Number(p?.attributes?.[ATTR_CLOCK]))
      }
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [room, adopt])
  useEffect(() => {
    if (!connected || anchorMs == null) return
    localParticipant.setAttributes({ [ATTR_CLOCK]: String(anchorMs) }).catch(() => {})
  }, [connected, anchorMs, localParticipant])
  const startClock = () => {
    if (anchorRef.current == null) adopt(Date.now())
  }

  // ── In-call commands ──
  const { send } = useDataChannel(CONTROL_TOPIC, (msg) => {
    try {
      const { type } = JSON.parse(new TextDecoder().decode(msg.payload))
      if (type === 'END') handlersRef.current.onRemoteEnd?.()
    } catch {
      /* not one of ours */
    }
  })
  const leave = () => {
    Promise.resolve(room.disconnect()).catch(() => {})
  }
  // Host: tell the other side the call is over, give the message a beat to
  // flush, then drop our own connection. The server closes the room itself
  // (onEndCall in the view), which is what ends a stubborn session.
  const endForAll = () => {
    try {
      send(new TextEncoder().encode(JSON.stringify({ type: 'END' })), { reliable: true })
    } catch {
      /* not connected */
    }
    window.setTimeout(leave, 250)
  }

  const report = (err) => handlersRef.current.onError?.(err)
  const toggleScreenShare = async () => {
    try {
      await localParticipant.setScreenShareEnabled(!isScreenShareEnabled, { audio: true })
    } catch (err) {
      // Closing the browser's picker is a NotAllowedError / AbortError — a
      // deliberate cancel, not a failure worth a toast.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') report(err)
    }
  }

  return {
    provider: 'livekit',
    phase,
    join: () => requestConnect(),
    leave,
    endForAll,
    registerHandlers: (handlers) => { handlersRef.current = handlers },
    localParticipantId,
    remoteParticipantId,
    presenterId,
    localMicOn: Boolean(isMicrophoneEnabled),
    localWebcamOn: Boolean(isCameraEnabled),
    localScreenShareOn: Boolean(isScreenShareEnabled),
    toggleMic: () => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled).catch(report),
    toggleWebcam: () => localParticipant.setCameraEnabled(!isCameraEnabled).catch(report),
    toggleScreenShare,
    clock: { anchorMs, start: startClock },
    Tile: LiveKitTile,
    ShareTile: LiveKitShareTile,
    RemoteAudio: null,
    DeviceSettingsButton: LiveKitDeviceSettingsButton,
  }
}

/* ── tiles ── */

/**
 * One participant's camera — the LiveKit twin of the VideoSDK tile, same
 * chrome. `fill` stretches it over the whole stage (the remote party);
 * `compact` is the small self-view riding the lower corner. `audioOnly`
 * never renders video and captions the stage with the audio-session notice.
 */
function LiveKitTile({ participantId, fill = false, compact = false, audioOnly = false }) {
  const { localParticipant } = useLocalParticipant()
  const remoteParticipants = useRemoteParticipants()
  const participant =
    localParticipant?.identity === participantId
      ? localParticipant
      : remoteParticipants.find((p) => p.identity === participantId) ?? null
  // useTracks re-renders on publish/mute changes, so camera/mic state is live.
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone], { onlySubscribed: false })
  const camRef = tracks.find((t) => t.participant.identity === participantId && t.source === Track.Source.Camera)
  const micRef = tracks.find((t) => t.participant.identity === participantId && t.source === Track.Source.Microphone)
  const isLocal = Boolean(participant?.isLocal)
  const webcamOn = Boolean(camRef?.publication && !camRef.publication.isMuted)
  const micOn = Boolean(micRef?.publication && !micRef.publication.isMuted)
  const displayName = participant?.name || participant?.identity || ''
  const showVideo = !audioOnly && webcamOn && Boolean(camRef)
  const videoRef = useRef(null)
  const portrait = useVideoPortrait(videoRef, showVideo)

  const frame = fill
    ? 'h-full w-full'
    : compact
      ? `w-full border-white/25 shadow-2xl ${portrait ? 'aspect-3/4' : 'aspect-video'}`
      : 'min-h-56'
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-[#1F1913] border border-white/10 ${frame}`}>
      {showVideo && (
        <div className="absolute inset-0">
          <VideoTrack
            trackRef={camRef}
            ref={videoRef}
            className={`w-full h-full ${fill ? 'object-contain' : 'object-cover'}`}
          />
        </div>
      )}
      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <div className={`rounded-full bg-linear-to-tr from-[#C8922A] to-[#E6C878] flex items-center justify-center font-cinzel font-bold text-[#1C140A] ${
            compact ? 'w-8 h-8 sm:w-9 sm:h-9 text-xs sm:text-sm' : 'w-12 h-12 sm:w-16 sm:h-16 text-xl sm:text-2xl'
          }`}>
            {(displayName || '?').trim().charAt(0).toUpperCase()}
          </div>
          {!compact && (audioOnly ? (
            <>
              <p className="flex items-center gap-2 font-cinzel text-base sm:text-lg text-white mt-3 sm:mt-4">
                <LuPhone className="w-4 h-4 shrink-0" /> Audio Consultation
              </p>
              <p className="text-[11px] sm:text-xs text-white/50 mt-1 max-w-xs">
                This is an audio-only session — video is disabled for both participants.
              </p>
            </>
          ) : (
            <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/50 mt-3">
              <LuVideoOff className="w-3.5 h-3.5" /> Camera off
            </p>
          ))}
        </div>
      )}
      <div className={`absolute max-w-[calc(100%-1rem)] flex items-center gap-1.5 sm:gap-2 rounded-full bg-black/50 backdrop-blur-sm ${
        compact ? 'bottom-1.5 left-1.5 px-2 py-0.5' : 'bottom-2 left-2 sm:bottom-2.5 sm:left-2.5 px-2.5 sm:px-3 py-1 sm:py-1.5'
      }`}>
        <span className={`truncate font-semibold text-white ${compact ? 'text-[9px]' : 'text-[10px] sm:text-[11px]'}`}>
          {compact && isLocal ? 'You' : `${displayName || 'Participant'}${isLocal ? ' (You)' : ''}`}
        </span>
        {!micOn && <LuMicOff className={`text-red-300 ${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} title="Muted" />}
      </div>
    </div>
  )
}

/** The presenter's shared screen, filling the stage (object-contain, never
 * cropped). Its audio, if any, plays through the room-level renderer. */
function LiveKitShareTile({ participantId }) {
  const shareTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: false })
  const trackRef = shareTracks.find((t) => t.participant.identity === participantId) ?? null
  const isLocal = Boolean(trackRef?.participant?.isLocal)
  const displayName = trackRef?.participant?.name || ''
  return (
    <div className="relative h-full w-full rounded-2xl overflow-hidden bg-black border border-white/10 flex items-center justify-center">
      {trackRef && <VideoTrack trackRef={trackRef} className="w-full h-full object-contain" />}
      <div className="absolute top-2 left-2 sm:top-2.5 sm:left-2.5 z-10 max-w-[calc(100%-1rem)] flex items-center gap-1.5 sm:gap-2 rounded-full bg-black/50 backdrop-blur-sm px-2.5 sm:px-3 py-1 sm:py-1.5">
        <LuScreenShare className="w-3.5 h-3.5 shrink-0 text-[#E6C878]" />
        <span className="truncate text-[10px] sm:text-[11px] font-semibold text-white">
          {isLocal ? 'You are sharing your screen' : `${displayName || 'Participant'} is sharing their screen`}
        </span>
      </div>
    </div>
  )
}
